import React from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, ShoppingCart, Trash2, X } from "lucide-react";
import { useCart } from "../../../context/CartContext";
import "./MiniCart.css";

const formatPrice = (value) => {
    const number = Number(value ?? 0);

    return new Intl.NumberFormat("fa-IR").format(
        Number.isFinite(number) ? number : 0,
    );
};

const getItemImage = (item) => {
    const variation = item?.selectedVariation;

    const images = item?.images;

    return (
        item?.image ||
        item?.image_url ||
        item?.thumbnail ||
        item?.thumbnail_url ||
        variation?.image ||
        variation?.image_url ||
        variation?.thumbnail ||
        (Array.isArray(images) ? images[0]?.url || images[0]?.src || images[0]?.path : null) ||
        null
    );
};

const getLowestPrice = (item) => {
    return Number(
        item?.lowestPrice ??
        item?.selectedVariation?.price ??
        item?.price ??
        item?.finalPrice ??
        0,
    );
};

const getFinalPrice = (item) => {
    return Number(
        item?.selectedVariation?.finalPrice ??
        item?.finalPrice ??
        item?.selectedVariation?.salePrice ??
        item?.lowestSalePrice ??
        getLowestPrice(item),
    );
};

const getItemName = (item) => {
    return item?.name || item?.title || item?.productName || "محصول";
};

const getVariationLabel = (item) => {
    const variation = item?.selectedVariation;

    if (!variation) {
        return null;
    }

    if (variation?.attributes?.value) {
        return variation.attributes.value;
    }

    return null;
};

function MiniCartItem({ item }) {
    const {
        increaseQty,
        decreaseQty,
        isValidating,
    } = useCart();

    const productId = item?.id;
    const variationId = item?.selectedVariation?.variationId ?? null;
    const quantity = Number(item?.qty ?? 1);
    const validating = isValidating?.(productId, variationId);
    const image = getItemImage(item);
    const lowestPrice = getLowestPrice(item);
    const finalPrice = getFinalPrice(item);
    const variationLabel = getVariationLabel(item);

    const handleIncrease = async (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (validating) return;
        await increaseQty(productId, variationId);
    };

    const handleDecrease = (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (validating) return;
        decreaseQty(productId, variationId);
    };

    return (
        <div className={`mini-cart-item${validating ? " is-validating" : ""}`}>
            <Link
                to={`/product/${item?.slug || item?.id}`}
                className="mini-cart-item__image"
                aria-label={`مشاهده ${getItemName(item)}`}
            >
                {image ? (
                    <img src={image} alt={getItemName(item)} />
                ) : (
                    <ShoppingCart size={24} />
                )}
            </Link>

            <div className="mini-cart-item__content">
                <Link
                    to={`/product/${item?.slug || item?.id}`}
                    className="mini-cart-item__name"
                >
                    {getItemName(item)}
                </Link>

                {variationLabel && (
                    <span className="mini-cart-item__variation">
                        {variationLabel}
                    </span>
                )}

                <div className="mini-cart-item__prices">


                    <span className={finalPrice !== lowestPrice ? 'mini-cart-item__lowest-price-th' : 'mini-cart-item__lowest-price' }>
                    {/* <span className="mini-cart-item__lowest-price"> */}
                        {formatPrice(lowestPrice)} <small>تومان</small>
                    </span>

                    {finalPrice !== lowestPrice && (
                        <span className="mini-cart-item__final-price">
                            {formatPrice(finalPrice)} <small>تومان</small>
                        </span>
                    )}


                </div>

                <div className="mini-cart-item__bottom">
                    <div
                        className="mini-cart-quantity"
                        aria-label={`تعداد ${getItemName(item)}`}
                    >
                        <button
                            type="button"
                            onClick={handleDecrease}
                            disabled={validating}
                            aria-label={quantity === 1 ? "حذف محصول" : "کاهش تعداد"}
                        >
                            {quantity === 1 ? <Trash2 size={13} /> : <Minus size={13} />}
                        </button>

                        <span>{formatPrice(quantity)}</span>

                        <button
                            type="button"
                            onClick={handleIncrease}
                            disabled={validating}
                            aria-label="افزایش تعداد"
                        >
                            <Plus size={13} />
                        </button>
                    </div>

                    {validating && (
                        <span className="mini-cart-item__loading">در حال بررسی...</span>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function MiniCart({ onClose }) {
    const {
        cart = [],
        finalPrice = 0,
    } = useCart();

    const itemCount = cart.reduce(
        (total, item) => total + Number(item?.qty || 0),
        0,
    );

    return (
        <div className="mini-cart" role="dialog" aria-label="سبد خرید کوچک">
            <div className="mini-cart__header">
                <div>
                    <strong>سبد خرید</strong>
                    <span>{formatPrice(itemCount)} محصول</span>
                </div>

                {onClose && (
                    <button
                        type="button"
                        className="mini-cart__close"
                        onClick={onClose}
                        aria-label="بستن سبد خرید"
                    >
                        <X size={17} />
                    </button>
                )}
            </div>

            {cart.length === 0 ? (
                <div className="mini-cart__empty">
                    <span className="mini-cart__empty-icon">
                        <ShoppingCart size={27} />
                    </span>
                    <strong>سبد خرید شما خالی است</strong>
                    <span>محصولات مورد نیازتان را به سبد خرید اضافه کنید.</span>
                </div>
            ) : (
                <>
                    <div className="mini-cart__items">
                        {cart.map((item) => {
                            const key = [
                                item?.id,
                                item?.selectedVariation?.variationId ?? "no-variation",
                                item?.selectedGift?.id ?? "no-gift",
                            ].join("-");

                            return <MiniCartItem item={item} key={key} />;
                        })}
                    </div>

                    <div className="mini-cart__footer">
                        <div className="mini-cart__total">
                            <span>مجموع</span>
                            <strong>
                                {formatPrice(finalPrice)} <small>تومان</small>
                            </strong>
                        </div>

                        <Link
                            to="/cart"
                            className="mini-cart__cart-button"
                            onClick={onClose}
                        >
                            مشاهده سبد خرید
                        </Link>
                    </div>
                </>
            )}
        </div>
    );
}
